import clsx from 'clsx';
import { useState } from 'react';
import Image from 'next/image';
import dayjs from 'dayjs';
import Link from 'next/link';

import { Icon } from 'common/icon';
import { useDeleteCasinoReviewsMutation } from 'hooks/api/casino_reviews';
import { ICasinoReview } from 'constants/types';
import { formatPluralWord } from 'utils/formats';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { Analytics } from 'constants/analytics';
import { StarRating } from 'components/star-rating';
import { Routes } from 'constants/routes';
import { ConfirmModal } from 'components/modal/confirm-modal';

import styles from './styles.module.css';

export const PanelReviewsCard = ({
  link,
  comment,
  isProfile,
  isPublicProfile,
}: {
  link?: string;
  isProfile?: boolean;
  isPublicProfile?: boolean;
  comment: ICasinoReview;
}) => {
  const [deleteCasinoReview] = useDeleteCasinoReviewsMutation();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const handleCancelDeletion = () => {
    setIsDeleteModalVisible(false);
  };

  const handleDelete = async () => {
    try {
      await deleteCasinoReview({ id: comment?._id });
    } catch (error) {
      console.error('Ошибка при удалении casino review', error);
    }
  };

  return (
    <>
      <div className={styles.reviews__column}>
        <div
          className={clsx(
            styles.casino_review,
            styles.casino_review_panel,
            styles.casino_review_recommended
          )}
        >
          <Link
            prefetch={false}
            id={
              isProfile
                ? Analytics.reviews.visitCasinoPage
                : Analytics.reviews.userPage
            }
            href={link ?? Routes.publicProfile(comment?.user?._id ?? '')}
            className={clsx(
              styles.casino_review__casino,
              styles.casino_review__casino_background
            )}
          >
            <div className={styles.casino_review__casino_info}>
              <div
                className={clsx(
                  styles.casino_review__casino_photo,
                  isProfile && styles.casino_review__casino_photo_profile
                )}
              >
                <LogoOverlayWrapper>
                  <LogoOverlay
                    width={50}
                    height={50}
                    alt={comment.data?.name}
                    image={comment?.data?.logoUrl}
                  />
                  <Image
                    src={
                      isProfile
                        ? comment?.data?.imageUrl || ''
                        : comment?.user?.imageUrl || ''
                    }
                    width={50}
                    height={50}
                    alt="review-image"
                  />
                </LogoOverlayWrapper>
              </div>
              <div
                className={clsx(
                  styles.casino_review__casino_about,
                  isProfile && styles.casino_review__center
                )}
              >
                <div className={styles.casino_review__casino_name}>
                  {isProfile
                    ? comment.data?.name
                    : (comment.user?.username ?? 'Username')}
                </div>
                {!isProfile && (
                  <div className={styles.casino_review__row}>
                    <div className={styles.casino_review__user_inspections}>
                      {formatPluralWord({
                        number: comment?.user?.inspections_count ?? 0,
                        word: 'review',
                      })}
                    </div>
                    <div className={styles.ovalContainer} />
                    <div className={styles.casino_review__user_inspections}>
                      {formatPluralWord({
                        number: comment?.user?.inspections_count ?? 0,
                        word: 'check',
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.casino_review__casino_arrow}>
              <Icon.Arrow fillOpacity="0.3" />
            </div>
          </Link>
          <div className={styles.casino_review__top_line}>
            <div
              className={clsx(
                styles.casino_review__row_info,
                isProfile && styles.casino_review__column
              )}
            >
              {comment?.rating ? (
                <StarRating
                  ratingNumber={comment.rating?.toString()}
                  starSize="15"
                  color={'#caf413'}
                  starGap="8.3px"
                />
              ) : (
                <StarRating
                  ratingNumber={'0'}
                  starSize="15"
                  color={'#caf413'}
                />
              )}
              <div className={styles.casino_review__date}>
                {dayjs(comment.createdAt).format('DD MMM, YYYY')}
              </div>
            </div>
            {!isPublicProfile && isProfile && (
              <button
                className={styles.binButton}
                onClick={() => setIsDeleteModalVisible(true)}
              >
                <Icon.Bin />
              </button>
            )}
          </div>

          <div className={styles.casino_review__text}>{comment.review}</div>
        </div>
      </div>
      <ConfirmModal
        headerTitle="Delete"
        isVisible={isDeleteModalVisible}
        onSubmit={handleDelete}
        onClose={handleCancelDeletion}
        title="Do you want to delete casino review?"
      />
    </>
  );
};
